//@ts-nocheck
import { Theme, ThemeProvider } from "@aws-amplify/ui-react";
import { SignOut } from "@aws-amplify/ui-react/dist/types/components/Authenticator/Authenticator";
import NavMenu from "@factorearth/component-library/dist/Molecules/NavMenu/NavMenu";
import { sprig } from "@sprig-technologies/sprig-browser";
import { generateClient } from "aws-amplify/api";
import FormGroupList from "components/FormGroupList/FormGroupList";
import Profile from "components/Profile/Profile";
import ProjectList from "components/ProjectList/ProjectList";
import SyncProject from "components/ProjectTab/SyncProject/SyncProject";
import styles from "components/RootContainer.module.scss";
import { EditPermission } from "components/TeamManager/EditPermissions/EditPermissions";
import TeamManager from "components/TeamManager/TeamManager";
import TemplateManager from "components/TemplateManager/TemplateManager";
import { version } from "components/VersionNumber/VersionNumber";
import { FormGroup, Project, User, UserForResolver } from "db/definitions";
import { getAllFormTemplates } from "db/queries/formTemplateQueries";
import useAutoSync from "hooks/useAutoSync";
import useDrawer, { Location } from "hooks/useDrawer";
import useForms from "hooks/useForms";
import useProjectConfigs from "hooks/useProjectConfig";
import useProjects from "hooks/useProjects";
import { useSingleton, useTheme } from "hooks/useSingleton";
import useUser from "hooks/useUser";
import { useAnalytics } from "hooks/useAnalytics";
import { NavigationAction, ProjectAction } from "types/analyticsStandards";
import React, { useEffect, useRef, useState } from "react";
import { FiAlertCircle, FiDownloadCloud, FiHome, FiRefreshCw, FiTable, FiUser, FiUsers } from "react-icons/fi";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import getFormTemplates from "utils/getFormTemplates";
import { detectDeviceType } from "utils/deviceDetection";
import Home from "./Home/Home";
import Tabs from "./Tabs/Tabs";
import { TemplateManagerProvider } from "hooks/useTemplateManager";
import DrawerTutorial from "./DrawerTutorial/DrawerTutorial";
import DrawerTutorialButton from "components/DrawerTutorial/DrawerTutorialButton/DrawerTutorialButton";
import { ConfirmationModal, ErrorSplash, GetFeedbackFnType, ReportABugButton, Typography, useProgress } from "@factorearth/component-library";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import Splash from "./Splash/Splash";
import ReleaseNotes from "./ReleaseNotes/ReleaseNotes";
import AllProjects from "./AllProjects/AllProjects";
import { ErrorProvider, useErrorContext } from "hooks/useErrorContext";
import * as Sentry from "@sentry/react";
import CreateNewProject from "./CreateNewProject/CreateNewProject";
import LoadingSpinner from "./LoadingSpinner/LoadingSpinner";
import { feedback } from "index";
import { AnalyticsDashboard } from "./AnalyticsDashboard/AnalyticsDashboard";

interface NewFormGroupData {
	name: string;
	templates: string[];
	type: "default" | "photolog";
}

sprig.configure({
	environmentId:
		import.meta.env.VITE_APP_ENV === "production" ? import.meta.env.VITE_APP_SPRIG_PRODUCTION_KEY : import.meta.env.VITE_APP_SPRIG_DEVELOPMENT_KEY,
});

const client = generateClient({ authMode: "userPool" });

const pathInfo: Record<string, Record<string, string>> = {
	"/team-manager": {
		name: "Team Manager",
		description: "Assign projects, roles, permissions, policies to your teams",
	},
	"/template-manager": {
		name: "Template Manager",
		description: "Edit existing templates or create and customize new templates",
	},
	"/sync-menu": {
		name: "Sync Menu",
		description: "Select a project to merge or take offline",
	},
	"/project": {
		// name: "Sync Menu",
		// description: "Select a Project to Merge or take Offline",
	},
	"/profile": {
		name: "Profile",
		description: "Your account profile, useful for troubleshooting.",
	},
	"/release-notes": {
		name: "Release Notes",
		description: "Features, bug fixes, and more included in the latest release",
	},
	"/all-projects": {
		name: "All Projects",
		description: "View all projects and manage them",
	},
};

interface RootContainerProps {
	estimateReadable: string | null;
	online: boolean;
	persisted: boolean;
	quota: number;
	quotaReadable: string;
	setUserOffline: (user: User) => Promise<void>;
	setUserOnline: () => Promise<void>;
	signOut?: SignOut;
	usage: number;
	usageReadable: string;
	userErrorMessage: string;
}

/**
 * Jah Root Container mon
 */
function RootContainer(props: RootContainerProps) {
	const { estimateReadable, online, persisted, quota, quotaReadable, setUserOffline, setUserOnline, signOut, usage, usageReadable, userErrorMessage } = props;

	const [loaded, setLoaded] = useState(false);
	const [selected, setSelected] = useState<string[]>([]);
	const [redirectToSyncMenu, setRedirectToSyncMenu] = useState(false);
	const [createNewToggle, setCreateNewToggle] = useState(false);
	const [offlineTemplatesToast, setOfflineTemplatesToast] = useState(false);
	const [createProjectOpen, setCreateProjectOpen] = useState(false);
	const { userDoc, handleSignOut: originalHandleSignOut, handleUpdateUser, fullUser } = useUser(online, signOut);

	// Wrap handleSignOut to include analytics session end
	const handleSignOut = async () => {
		try {
			// End analytics session before signing out
			await analytics.endSession();
		} catch (error) {
			console.error("Failed to end analytics session on logout:", error);
		} finally {
			// Always proceed with original sign out
			originalHandleSignOut();
		}
	};
	const { projectConfigs } = useProjectConfigs(userDoc);
	const { handleAutoUpdate } = useAutoSync(projectConfigs);
	const [isSuperUser, setIsSuperUser] = useState<boolean>(false);
	const [isProjectManager, setIsProjectManager] = useState<boolean>(false);
	const { handleThemePreferences, setMode } = useSingleton();
	const location = useLocation();
	const color = useTheme();
	const navigate = useNavigate();
	const { drawer, setupDrawer } = useDrawer();
	// Track route changes (separate from page loads)
	const prevLocationRef = useRef(location.pathname);
	// Track session initialization to prevent duplicates
	const sessionInitializedRef = useRef(false);

	const {
		createProject,
		deleteProject,
		getProject,
		getProjects,
		loadingProject,
		project,
		projectId,
		projects,
		recentProjects,
		setProjectId,
		updateProject,
		updateProjectsSyncMenu,
	} = useProjects(userDoc, online);

	const { progress, setProgress } = useProgress();

	const {
		addFormGroupsInState,
		createNewFormGroup,
		addFormGroup,
		handleClearAllTabs,
		deleteSelectedFormGroups,
		fetchFormGroupsForState,
		addFormTemplateToListInState,
		removeTemplateFromListInState,
		formTemplates,
		formGroups,
		tabs,
		removeTab,
		handleRefreshTabs,
	} = useForms(project, online, userDoc as User, setupDrawer, setProgress);

	// Initialize analytics hook for tracking user events - always call hook (React rules)
	const analytics = useAnalytics({
		userId: userDoc?.id || "",
		groupId: userDoc?.group || "",
		online,
		autoStart: false, // Disabled: we'll handle session lifecycle manually below
	});

	// Track route changes for analytics activity.
	// For /project/{id} routes, only emit a project_open event when first navigating
	// INTO a project (i.e. the project ID changes). Sub-route changes within the same
	// project (formgroups, etc.) do not emit additional events.
	useEffect(() => {
		const currentPath = location.pathname;
		const previousPath = prevLocationRef.current;

		// Don't track initial load, only route changes
		if (previousPath !== currentPath && userDoc && analytics.isEnabled) {
			const projectRootMatch = currentPath.match(/^\/project\/([^\/]+)/);
			const prevProjectRootMatch = previousPath ? previousPath.match(/^\/project\/([^\/]+)/) : null;
			const currentProjectId = projectRootMatch?.[1];
			const prevProjectId = prevProjectRootMatch?.[1];

			if (currentProjectId && currentProjectId !== prevProjectId) {
				// Navigating into a new project — emit project_open once
				analytics
					.trackProjectEvent(ProjectAction.PROJECT_OPEN, {
						projectId: currentProjectId,
						previousRoute: previousPath,
						timestamp: new Date().toISOString(),
						online: online,
					})
					.catch((error) => {
						console.error("Failed to track project open:", error);
					});
			} else if (!currentProjectId) {
				// Non-project route change — track normally
				analytics
					.trackRouteChange(currentPath, {
						previousRoute: previousPath,
						timestamp: new Date().toISOString(),
						online: online,
					})
					.catch((error) => {
						console.error("Failed to track route change:", error);
					});
			}
			// Sub-route changes within the same project are intentionally not tracked
		}

		// Update the ref for next change
		prevLocationRef.current = currentPath;
	}, [location.pathname, userDoc, analytics, online]);

	// Debug log to see analytics state

	useEffect(() => {
		setSelected([]);
		handleClearAllTabs();
	}, [projectId]);

	useEffect(() => {
		handleRefreshTabs();
	}, [color.theme]);

	const theme: Theme = {
		name: "global-theme",
		tokens: {
			components: {
				button: {
					borderRadius: { value: "0px 4px 4px 0px" },
					paddingBlockStart: { value: "13px" },
					paddingBlockEnd: { value: "13px" },
					backgroundColor: { value: color.background.primary as string },
					borderColor: { value: color.border.secondary as string },
					_active: {
						backgroundColor: { value: color.background.primary as string },
						borderColor: { value: color.border.secondary as string },
					},
					_focus: {
						backgroundColor: { value: color.background.primary as string },
						borderColor: { value: color.border.secondary as string },
					},
				},
				menu: {
					backgroundColor: { value: color.highlight.secondary as string },
					borderStyle: { value: "none" },
					flexDirection: { value: "row" },
					minWidth: { value: "100%" },
					item: {
						minHeight: { value: "10px" },
					},
				},
				fieldcontrol: {
					borderColor: { value: color.border.primary },
					_focus: {
						borderColor: { value: color.border.primary },
					},
				},
				input: {
					color: { value: color.text.primary },
					borderColor: { value: color.border.primary },
					_focus: {
						borderColor: { value: color.border.tertiary },
					},
				},
				select: {
					backgroundColor: { value: color.background.primary as string },
					color: { value: color.text.primary as string },
					// _focus: {
					// 	borderColor: { value: color.border.tertiary as string },
					// },
					iconWrapper: {
						// color: { value: color.text.primary as string },
						large: {
							// value: "24px"
						},
					},
				},
				icon: {
					// color: { value: color.text.primary as string },
				},
				stepperfield: {
					flexDirection: { value: "column" },
					input: {
						color: { value: color.text.primary },
						// borderColor: { value: color.border.primary },
						// _focus: {
						// 	color: { value: color.border.tertiary },
						// 	backgroundColor: { value: color.background.secondary },
						// },
					},
					button: {
						color: { value: color.text.primary },
						backgroundColor: { value: color.background.primary },
						// borderColor: { value: color.border.primary },
						// borderWidth: { value: "1px" },
					},
				},
				phonenumberfield: {
					color: { value: color.text.primary },
				},
			},
		},
	};

	useEffect(() => {
		if (fullUser) {
			if (fullUser?.roles) {
				const isSuperUser = fullUser.roles.find((role) => role.name === "Super User");
				const isProjectManager = fullUser.roles.find((role) => role.name === "Project Manager");
				if (isSuperUser) {
					setIsSuperUser(true);
				}
				if (isProjectManager) {
					setIsProjectManager(true);
				}
			}

			handleThemePreferences(fullUser);
			if (!loadingProject) setLoaded(true);
		} else if (fullUser === undefined && !online) {
			// SPECIFICALLY UNDEFINED. This means that the user is offline with no user. We have to allow them online to sign in I think
			setUserOnline();
		}
	}, [fullUser]);

	useEffect(() => {
		setLoaded(!loadingProject);
	}, [loadingProject]);

	function handleUserMode(boolean: boolean) {
		window.localStorage.setItem("theme", boolean ? "light" : "dark");
		const userObj = {
			...userDoc,
			lightMode: boolean,
		};
		setMode(boolean);
		handleUpdateUser(userObj as User);
	}

	function handleToggleOnline() {
		if (online) {
			if (fullUser) {
				let theme = localStorage.getItem("theme") as "light" | "dark" | null | undefined;
				if (theme === null || theme === undefined) {
					localStorage.setItem("theme", "light");
					theme = "light";
				}
				fullUser.lightMode = theme === "light" ? true : false;
				setUserOffline(fullUser);
				handleClearAllTabs();
			}
		} else {
			setUserOnline();
			handleClearAllTabs();
		}
	}

	/**
	 * Initialize session when user is loaded
	 * NOTE: This is the primary session manager for the application.
	 * The AnalyticsProcessor handles backend session determination and aggregation.
	 * @param fullUser - The fully loaded user object with ID and group information
	 */
	function initializeSession(fullUser: User) {
		// Guard against duplicate initialization in this component instance
		if (sessionInitializedRef.current) {
			return;
		}

		// Check if there's a recent session in sessionStorage (persists across page refreshes)
		const sessionKey = `analytics_session_${fullUser.id}`;
		const lastSessionStart = sessionStorage.getItem(sessionKey);
		const now = Date.now();
		const sessionTimeout = 30 * 60 * 1000; // 30 minutes in milliseconds

		// If there was a recent session (within 30 minutes), don't start a new one
		if (lastSessionStart && now - parseInt(lastSessionStart) < sessionTimeout) {
			sessionInitializedRef.current = true;
			// Note: Analytics service will automatically reuse the same sessionId for events
			return;
		}

		// Start only analytics session (remove duplicate simple session)
		analytics.startSession().catch((error) => {
			console.error("Failed to start analytics session:", error);
		});

		// Mark as initialized to prevent re-runs
		sessionInitializedRef.current = true;

		// Store session start time (sessionId is handled by analytics service)
		sessionStorage.setItem(sessionKey, now.toString());
	}

	// This useEffect and function are to check to see if even a single form template exists offline,
	// if not they need to be prompted or redirected to save templates in the TSM.
	useEffect(() => {
		let isMounted = true;
		async function checkOfflineTemplates() {
			const retrievedOfflineTemplates = await getAllFormTemplates();
			if (retrievedOfflineTemplates.length < 1) {
				setOfflineTemplatesToast(true);
			}
		}

		let autosyncPromise: Promise<any> | null = null;

		async function checkAutoSync() {
			autosyncPromise = getFormTemplates();
			const retrievedTemplates = await autosyncPromise;

			// Sync analytics events alongside template sync
			if (analytics.isEnabled) {
				analytics.syncAnalytics().catch((error) => {
					console.error("Analytics sync failed during auto sync:", error);
				});
			}

			if (isMounted) handleAutoUpdate(retrievedTemplates);
		}

		if (userDoc && !online) {
			checkOfflineTemplates();
		} else if (online && userDoc) {
			checkAutoSync();
		}

		// Simple page tracking - let analytics service handle deduplication
		if (userDoc && analytics.isEnabled) {
			// Simplified refresh detection
			const isRefresh = performance.getEntriesByType("navigation")[0]?.type === "reload";
			const action = isRefresh ? NavigationAction.REFRESH : NavigationAction.PAGE_VIEW;

			analytics
				.trackNavigationEvent(action, {
					route: location.pathname,
					online: online,
					loadType: isRefresh ? "refresh" : "initial",
				})
				.catch((error) => {
					console.error("Failed to track navigation event:", error);
				});

			// Initialize session (analytics service handles deduplication)
			initializeSession(userDoc as User);
		}

		return () => {
			isMounted = false;
			if (autosyncPromise) client.cancel(autosyncPromise, "RootContainer unmounted");
			// Reset session tracking on unmount
			sessionInitializedRef.current = false;
		};
	}, [userDoc, analytics.isEnabled]);

	function handleChangeProject(e: { target: { label?: string; value: string } }) {
		const { value } = e.target;
		setProjectId(value);
	}

	const locations = [
		{
			name: "Home",
			onClick: () => {
				navigate("/");
				setProjectId("");
			},
			reqPerm: "",
			icon: <FiHome />,
		},
		{
			name: "Profile",
			onClick: () => {
				navigate(`/profile/${userDoc?.id}`);
			},
			reqPerm: "",
			icon: <FiUser />,
		},
		{
			name: "Refresh",
			onClick: () => {
				window.location.reload();
			},
			reqPerm: "",
			icon: <FiRefreshCw />,
		},
		{
			name: "",
			reqPerm: "",
			onClick: () => {
				if (document.getElementById("sentry-feedback-button")) {
					document.getElementById("sentry-feedback-button")?.click();
				}
			},
			icon: (
				<ReportABugButton
					variant="outlined"
					colorPalette={color}
					style={{ border: "none", fontSize: "16px", padding: "0" }}
					onClick={async () => {
						const form = await feedback.createForm();
						form.appendToDom();
						form.open();
					}}
				/>
			),
		},
		// {
		// 	name: "FAQ's (Coming Soon!)",
		// 	onClick: () => {},
		// 	reqPerm: "",
		// 	icon: <FiHelpCircle />,
		// },
		// {
		// 	name: "Admin",
		// 	onClick: () => navigate("/admin"),
		// 	reqPerm: "dev",
		// },
		// {
		//   name: "Inbox",
		//   onClick: () => setInboxOpen(true),
		//   onClick: () => {<Link to="/inbox"/>},
		//   reqPerm: "",
		//   icon: <FiInbox />
		// },
	];

	if (online && !isSuperUser && !isProjectManager) {
		locations.splice(1, 0, {
			name: "Sync Menu",
			onClick: () => navigate("/sync-menu"),
			reqPerm: "sync menu",
			icon: <FiDownloadCloud />,
		});
	}
	if (online && isSuperUser) {
		locations.splice(
			1,
			0,
			{
				name: "Sync Menu",
				onClick: () => navigate("/sync-menu"),
				reqPerm: "sync menu",
				icon: <FiDownloadCloud />,
			},
			{
				name: "Team Manager",
				onClick: () => navigate("/team-manager"),
				reqPerm: "team manager",
				icon: <FiUsers />,
			},
			{
				name: "Template Manager",
				onClick: () => navigate("/template-manager"),
				reqPerm: "template manager",
				icon: <FiTable />,
			},
			{
				name: "Analytics Dashboard",
				onClick: () => navigate("/analytics"),
				reqPerm: "template manager", // setting to template manager until the actual analytics role is setup
				icon: <FiTable />,
			},
		);
	} else if (online && isProjectManager) {
		locations.splice(
			1,
			0,
			{
				name: "Sync Menu",
				onClick: () => navigate("/sync-menu"),
				reqPerm: "sync menu",
				icon: <FiDownloadCloud />,
			},
			// ,
			// {
			// 	name: "Template Manager",
			// 	onClick: () => navigate("/template-manager"),
			// 	reqPerm: "template manager",
			// 	icon: <FiTable />,
			// },
		);
	}

	const appInfo = {
		name: import.meta.env.VITE_APP_ENV !== "production" ? `${import.meta.env.VITE_APP_ENV} Record` : "Record",
		versionNum: version,
		versionNumOnClick: () => {
			navigate("/release-notes");
		},
		headerLogo: "/FELogoOnly.png",
		logoOnClick: () => {
			setProjectId("");
			navigate("/");
		},
		menuLogo: "/FELogoStackedText.png",
		menuLogoDark: "/FELogoStackedWhite.png",
		copyRight: `©${new Date().getFullYear()} Factor Earth Record™`,
		dropdown: {
			dropdownComponent: (
				<ProjectList
					color={color}
					online={online}
					getProjects={getProjects}
					projects={projects as Project[]}
					recentProjects={recentProjects}
					handleChangeProject={handleChangeProject}
					projectId={projectId}
					navMenu
					header={<Typography textColor={color.text.primary} variant="Label" content="Projects" />}
					setCreateProjectOpen={setCreateProjectOpen}
				/>
			),
		},
		dropdownOptions: [],
		offlineCapable: true,
		theme: color.theme === "light" ? true : false,
		setTheme: handleUserMode,
		online: online,
		setOnline: handleToggleOnline,
	};

	if (location.pathname.includes("tabs")) {
		// @ts-ignore
		appInfo["headerRightContent"] = (
			<div className={styles.headerRightContent}>
				<DrawerTutorialButton helpColor={color.buttonText.outlined} location={"form-group"} setupDrawer={setupDrawer} style={{ border: "0px" }} />
			</div>
		);
	} else if (location.pathname.includes("project") && location.pathname !== "/all-projects") {
		// @ts-ignore
		appInfo["headerRightContent"] = (
			<div className={styles.headerRightContent}>
				<DrawerTutorialButton helpColor={color.buttonText.outlined} location={"project-list"} setupDrawer={setupDrawer} style={{ border: "0px" }} />
			</div>
		);
	} else if (location.pathname === "/") {
		// @ts-ignore
		appInfo["headerRightContent"] = (
			<div className={styles.headerRightContent}>
				<DrawerTutorialButton helpColor={color.buttonText.outlined} location={"home"} setupDrawer={setupDrawer} style={{ border: "0px" }} />
			</div>
		);
	} else if (location.pathname.includes("profile")) {
		// @ts-ignore
		appInfo["headerRightContent"] = (
			<div className={styles.headerRightContent}>
				<div className={styles.rightContentTitleAndHelp}>
					<h2 style={{ all: "unset", margin: "0px", color: color.text.primary }}>{pathInfo["/profile"].name}</h2>
					<DrawerTutorialButton helpColor={color.buttonText.outlined} location={"profile"} setupDrawer={setupDrawer} style={{ border: "0px" }} />
				</div>
				<p style={{ all: "unset", color: color.text.primary }} className={styles.rightContentDescription}>
					{pathInfo["/profile"].description}
				</p>
			</div>
		);
	} else if (pathInfo[location.pathname]) {
		// @ts-ignore
		appInfo["headerRightContent"] = (
			<div className={styles.headerRightContent}>
				<div className={styles.rightContentTitleAndHelp}>
					<h2 style={{ all: "unset", margin: "0px", color: color.text.primary }}>{pathInfo[location.pathname].name}</h2>
					<DrawerTutorialButton
						helpColor={color.buttonText.outlined}
						location={window.location.pathname.split("/")[1] as Location}
						setupDrawer={setupDrawer}
						style={{ border: "0px" }}
					/>
				</div>
				<p style={{ all: "unset", color: color.text.primary }} className={styles.rightContentDescription}>
					{pathInfo[location.pathname].description}
				</p>
			</div>
		);
	} else if (location.pathname.includes("project") && location.pathname !== "/all-projects") {
		// @ts-ignore
		appInfo["headerRightContent"] = (
			<div className={styles.headerRightContent}>
				<DrawerTutorialButton helpColor={color.buttonText.outlined} location={"project-list"} setupDrawer={setupDrawer} style={{ border: "0px" }} />
			</div>
		);
	} else if (location.pathname === "/") {
		// @ts-ignore
		appInfo["headerRightContent"] = (
			<div className={styles.headerRightContent}>
				<DrawerTutorialButton helpColor={color.buttonText.outlined} location={"home"} setupDrawer={setupDrawer} style={{ border: "0px" }} />
			</div>
		);
	} else if (location.pathname === "/analytics") {
		// @ts-ignore
		// appInfo["headerRightContent"] = (
		// 	<div className={styles.headerRightContent}>
		// 		<AnalyticsDashboard />
		// 	</div>
		// );
	} else if (pathInfo[location.pathname]) {
		// @ts-ignore
		appInfo["headerRightContent"] = (
			<div className={styles.headerRightContent}>
				<div className={styles.rightContentTitleAndHelp}>
					<h2 style={{ all: "unset", margin: "0px", color: color.text.primary }}>{pathInfo[location.pathname].name}</h2>
					<DrawerTutorialButton
						helpColor={color.buttonText.outlined}
						location={window.location.pathname.split("/")[1] as Location}
						setupDrawer={setupDrawer}
						style={{ border: "0px" }}
					/>
				</div>
				<p style={{ all: "unset", color: color.text.primary }} className={styles.rightContentDescription}>
					{pathInfo[location.pathname].description}
				</p>
			</div>
		);
	}

	function handleCreateNewClick() {
		setCreateNewToggle(true);
	}

	function closeCreateNew() {
		setCreateNewToggle(false);
	}

	async function submitCreate(newFormGroup: NewFormGroupData): Promise<FormGroup | undefined> {
		// NEED TO CHANGE THIS TO USE DYNAMIC PROJECT
		const formgroup = await createNewFormGroup(newFormGroup);
		return formgroup;
	}

	async function handleFormGroupOpen(data: Pick<FormGroup, "id" | "name"> & Partial<FormGroup>) {
		const formGroupData = {
			id: data.id,
			name: data.name,
		};
		addFormGroup(formGroupData);
	}

	const handleRedirectToSyncMenu = () => {
		setUserOnline();
		setRedirectToSyncMenu(true);
		setOfflineTemplatesToast(false);
		navigate("/sync-menu");
	};

	const ErrorFallback = (caughtError: FallbackProps) => {
		const { error, resetError, setError } = useErrorContext();
		const color = useTheme();
		const navigate = useNavigate();

		setError(caughtError.error);
		// This boundary sits inside Sentry's, so without this the crash the user is looking at never reaches Sentry.
		useEffect(() => {
			Sentry.withScope((scope) => {
				scope.setTag("error-boundary", "RootContainer");
				scope.setExtra("route", location.pathname);
				scope.setExtra("projectId", projectId);
				Sentry.captureException(caughtError.error);
			});
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [caughtError.error]);

		return (
			<div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
				<ErrorSplash
					colorPalette={color}
					// title={"Ooops! Something went wrong."}
					// errorMessage={(error && error.message) || "An unexpected error occurred."}
					// description=""
					backgroundImage={"/topo.svg"}
					onTryAgain={() => {
						resetError();
						navigate("/");
						window.location.reload();
					}}
					onReportBug={async () => {
						const form = await feedback.createForm();
						form.appendToDom();
						form.open();
					}}
				/>
			</div>
		);
	};

	return loaded ? (
		<ErrorProvider>
			<ErrorBoundary FallbackComponent={ErrorFallback}>
				{offlineTemplatesToast && (
					<ConfirmationModal
						colorPalette={color}
						details="You currently have no templates saved offline for this device! This will be an issue when you try to record data in forms while out of
					network service. Press `Ok` to be redirected to the Template Sync Menu, where you can select which Templates you would like to save
					for offline use."
						confirmTitle={
							<Typography variant="Heading 3" textColor={color.text.primary}>
								Redirect To Template Sync Menu
							</Typography>
						}
						open={offlineTemplatesToast}
						setOpen={setOfflineTemplatesToast}
						handleSubmit={handleRedirectToSyncMenu}
						timeout={5000}
						canClose
					/>
				)}
				{userDoc && createProjectOpen && (
					<CreateNewProject
						group={userDoc.group}
						handleCreateProject={createProject}
						projects={projects}
						formTemplates={formTemplates}
						online={online}
						open={createProjectOpen}
						setOpen={setCreateProjectOpen}
					/>
				)}
				<div
					style={{
						backgroundColor: color.background.absolute,
						backgroundImage: 'url("/topo.svg")',
						backgroundSize: "cover",
						backgroundRepeat: "no-repeat",
						width: "100%",
						display: "flex",
					}}>
					<ThemeProvider theme={theme} colorMode={color.theme}>
						{/* @ts-ignore */}
						<NavMenu colorPalette={color} locations={locations} appInfo={appInfo} />
						<Routes>
							<Route
								path="/"
								element={
									<Home
										colorPalette={color}
										createProject={createProject}
										getProjects={getProjects}
										handleChangeProject={handleChangeProject}
										online={online}
										projectId={projectId}
										projects={projects}
										recentProjects={recentProjects}
										userDoc={userDoc}
										formTemplates={formTemplates}
									/>
								}
							/>
							<Route path="/release-notes" element={online ? <ReleaseNotes version={version} /> : <Navigate to="/" replace />} />
							<Route path="/team-manager" element={online ? <TeamManager userDoc={userDoc} online={online} /> : <Navigate to="/" replace />} />
							<Route
								path="/team-manager/edit-user-permissions/:id"
								element={online ? <EditPermission online={online} /> : <Navigate to="/" replace />}
							/>
							<Route
								path="/sync-menu"
								element={
									online ? (
										<SyncProject
											addFormGroupsInState={addFormGroupsInState}
											color={color}
											estimateReadable={estimateReadable}
											formGroups={formGroups}
											formTemplates={formTemplates}
											online={online}
											persisted={persisted}
											project={project}
											projectId={projectId}
											projects={projects}
											recentProjects={recentProjects}
											quota={quota}
											quotaReadable={quotaReadable}
											redirectToSyncMenu={redirectToSyncMenu}
											selected={selected}
											setProjectId={setProjectId}
											setSelected={setSelected}
											setupDrawer={setupDrawer}
											updateProjectsSyncMenu={updateProjectsSyncMenu}
											usage={usage}
											usageReadable={usageReadable}
											user={userDoc}
											userErrorMessage={userErrorMessage}
										/>
									) : (
										<Navigate to="/" replace />
									)
								}
							/>
							<Route
								path={"/template-manager"}
								element={
									online && fullUser ? (
										<TemplateManagerProvider initialData={{ online, projectId, formTemplates }}>
											<TemplateManager
												addFormTemplateToListInState={addFormTemplateToListInState}
												formTemplates={formTemplates}
												getProjects={getProjects}
												online={online}
												projectId={projectId}
												projects={projects}
												recentProjects={recentProjects}
												removeTemplateFromListInState={removeTemplateFromListInState}
												setProjectId={setProjectId}
												setupDrawer={setupDrawer}
												userDoc={fullUser}
											/>
										</TemplateManagerProvider>
									) : (
										<Navigate to="/" replace />
									)
								}
							/>
							{/* <Route path="/admin" element={<Admin color={color}/>} /> */}
							<Route path="/profile/:userId" element={<Profile signOut={handleSignOut} online={online} user={fullUser} version={version} />} />
							{/* <Route path="/system" element={<System projects={projects} />} /> */}
							{/* <Route path="/inbox" element={<></>} /> */}
							<Route
								path="/project/:projectId"
								element={
									<FormGroupList
										setupDrawer={setupDrawer}
										color={color}
										user={userDoc ? userDoc : ({} as UserForResolver)}
										formGroups={formGroups}
										getSingleFormGroup={handleFormGroupOpen}
										selected={selected}
										closeCreateNew={closeCreateNew}
										handleCreateNewClick={handleCreateNewClick}
										createNewToggle={createNewToggle}
										formTemplates={formTemplates}
										submitCreate={submitCreate}
										setSelected={setSelected}
										deleteSelectedFormGroups={deleteSelectedFormGroups}
										online={online}
										deleteProject={deleteProject}
										updateProject={updateProject}
										getProject={getProject}
										fetchFormGroupsForState={fetchFormGroupsForState}
										addFormGroupsInState={addFormGroupsInState}
										updateProjectsSyncMenu={updateProjectsSyncMenu}
										persisted={persisted}
									/>
								}
							/>
							<Route
								path="/project/:projectId/tabs/:tab"
								element={
									project ? (
										<Tabs project={project} color={color} tabs={tabs} removeTab={removeTab} progress={progress} setProgress={setProgress} />
									) : (
										<LoadingSpinner />
									)
								}
							/>
							<Route
								path="/all-projects"
								element={
									<AllProjects
										projects={projects as Project[]}
										createProject={createProject}
										userDoc={fullUser}
										online={online}
										formTemplates={formTemplates}
										updateProjectsSyncMenu={updateProjectsSyncMenu}
										addFormGroupsInState={addFormGroupsInState}
									/>
								}
							/>
							// PWAs on IOS initialize with the pathname as /index.html, this conditional will navigate to the root path
							<Route path="/index.html" element={<Navigate to="/" replace />} />
							<Route
								path="*"
								element={
									<ErrorSplash
										colorPalette={color}
										backgroundImage={"/topo.svg"}
										onTryAgain={() => {
											navigate("/");
										}}
										// onReportBug={ async () => {
										// 	const form = await feedback.createForm();
										// 	form.appendToDom();
										// 	form.open();
										// }}
									/>
								}
							/>
							<Route path="/analytics" element={online ? <AnalyticsDashboard /> : <Navigate to="/" replace />} />
						</Routes>
					</ThemeProvider>
					{drawer && <DrawerTutorial color={color} location={drawer} setupDrawer={setupDrawer} />}
				</div>
			</ErrorBoundary>
		</ErrorProvider>
	) : (
		<Splash />
	);
}

export default RootContainer;
